import type { ShipperApplicationInput, ShipperDocument, ShipperProfileView, ShipperShift, ShipperSignupOptions, VehicleType } from '@khaddi/contracts';
import { Button, Chip, ErrorNote, Field, FooterBar, IconButton, LogoMark, Screen, Spinner, Text, TopBar, colors, radii, useAction, useApi, useAsync, useSession, brandSurface } from '@khaddi/ui';
import { useState } from 'react';
import { View } from 'react-native';
import { DocumentTile, FieldLabel, ShiftButton, StepIndicator, TermsCheck, SIGNUP_STEPS } from '../components/SignupParts';

export type SignupScreenProps = {
  /** A rejected application, used to prefill the form when re-applying. */
  previous: ShipperProfileView | null;
  onSubmitted: (profile: ShipperProfileView) => void;
  onCancel?: () => void;
};

type Draft = {
  name: string;
  vehicleType: VehicleType;
  vehicleModel: string;
  plate: string;
  areas: string[];
  shifts: ShipperShift[];
  documents: Partial<Record<ShipperDocument, string>>;
  bankAccount: string;
  acceptTerms: boolean;
};

const toggleIn = <T,>(list: T[], item: T): T[] => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

const infoErrors = (draft: Draft): string[] =>
  [
    draft.name.trim().length < 2 ? 'Nhập họ tên (ít nhất 2 ký tự)' : null,
    draft.vehicleType !== 'Xe đạp' && !draft.plate.trim() ? 'Nhập biển số xe' : null,
    draft.areas.length === 0 ? 'Chọn ít nhất 1 khu vực' : null,
    draft.shifts.length === 0 ? 'Chọn ít nhất 1 ca' : null,
  ].filter((e): e is string => e !== null);

const LAST_STEP = SIGNUP_STEPS.length - 1;

export const SignupScreen = ({ previous, onSubmitted, onCancel }: SignupScreenProps) => {
  const api = useApi();
  const { user, signOut } = useSession();
  const options = useAsync(() => api.shipper.signupOptions(), []);
  const [step, setStep] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => ({
    name: previous?.name ?? (user?.name && !/^\d+$/.test(user.name) ? user.name : ''),
    vehicleType: previous?.vehicleType ?? 'Xe máy',
    vehicleModel: previous?.vehicleModel ?? '',
    plate: previous?.plate ?? '',
    areas: previous?.areas ?? [],
    shifts: previous?.shifts ?? [],
    documents: {},
    bankAccount: '',
    acceptTerms: false,
  }));
  const update = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const submit = useAction(async () => {
    const bankAccount = draft.bankAccount.trim() || null;
    const input: ShipperApplicationInput = {
      name: draft.name.trim(),
      vehicleType: draft.vehicleType,
      vehicleModel: draft.vehicleModel.trim(),
      plate: draft.plate.trim(),
      areas: draft.areas,
      shifts: draft.shifts,
      // the bank account doubles as the `bankAccount` document the store checks for
      documents: bankAccount ? { ...draft.documents, bankAccount } : draft.documents,
      bankAccount,
      acceptTerms: true,
    };
    onSubmitted(await api.shipper.apply(input));
  });

  const errors = step === 0 ? infoErrors(draft) : step === LAST_STEP && !draft.acceptTerms ? ['Cần đồng ý quy định hợp tác giao hàng'] : [];

  const next = () => {
    setShowErrors(true);
    if (errors.length) return;
    setShowErrors(false);
    if (step < LAST_STEP) setStep(step + 1);
    else void submit.run();
  };
  const back = step > 0 ? () => setStep(step - 1) : onCancel;

  return (
    <View style={{ flex: 1, backgroundColor: colors.surface }}>
      <TopBar
        title="Đăng ký làm shipper"
        onBack={back}
        background={colors.surface}
        right={<IconButton icon="logout" label="Đăng xuất" onPress={signOut} background="transparent" />}
      />
      <Screen
        background={colors.surface}
        maxWidth={720}
        footer={
          <FooterBar style={{ paddingHorizontal: 20 }}>
            <TermsCheck checked={draft.acceptTerms} onChange={(acceptTerms) => update({ acceptTerms })} />
            {showErrors && errors.length ? (
              <View accessibilityRole="alert"><Text variant="caption" color={colors.danger}>
                {errors.join(' · ')}
              </Text></View>
            ) : null}
            {submit.error ? (
              <View accessibilityRole="alert"><Text variant="caption" color={colors.danger}>
                {submit.error}
              </Text></View>
            ) : null}
            <Button label={step < LAST_STEP ? 'Tiếp tục' : 'Gửi hồ sơ'} onPress={next} loading={submit.pending} disabled={!options.data} />
          </FooterBar>
        }
        contentStyle={{ paddingHorizontal: 20, gap: 16, paddingBottom: 180 }}
      >
        <IntroCard />
        <StepIndicator step={step} />
        {options.loading && !options.data ? <Spinner /> : null}
        {options.error && !options.data ? <ErrorNote message={options.error} onRetry={() => void options.reload()} /> : null}
        {options.data && step === 0 ? <InfoStep options={options.data} draft={draft} update={update} /> : null}
        {options.data && step === 1 ? <DocumentsStep options={options.data} draft={draft} update={update} /> : null}
        {options.data && step === 2 ? <PayoutStep draft={draft} update={update} /> : null}
      </Screen>
    </View>
  );
};

const IntroCard = () => (
  <View style={{ ...brandSurface, borderRadius: 20, padding: 16, overflow: 'hidden' }}>
    <View aria-hidden style={{ position: 'absolute', right: -30, bottom: -40 }}>
      <LogoMark size={140} opacity={0.14} />
    </View>
    <View style={{ gap: 4 }}>
      <Text variant="title" color={colors.text} style={{ fontSize: 20, lineHeight: 23 }}>
        Giao trà cùng Khao Ddi
      </Text>
      <Text variant="caption" color={colors.text} style={{ lineHeight: 18 }}>
        Chủ động giờ chạy · nhận tiền theo ngày · đơn gần quán, quãng ngắn
      </Text>
    </View>
  </View>
);

type StepProps = { options: ShipperSignupOptions; draft: Draft; update: (patch: Partial<Draft>) => void };

const InfoStep = ({ options, draft, update }: StepProps) => (
  <>
    <Text variant="heading" style={{ fontSize: 16 }}>
      Thông tin & phương tiện
    </Text>
    <Field label="Họ và tên" placeholder="Nguyễn Văn A" value={draft.name} onChangeText={(name) => update({ name })} autoComplete="name" />
    <View>
      <FieldLabel>Loại xe</FieldLabel>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {options.vehicleTypes.map((type) => (
          <Chip key={type} label={type} selected={draft.vehicleType === type} onPress={() => update({ vehicleType: type })} height={44} />
        ))}
      </View>
    </View>
    <View style={{ flexDirection: 'row', gap: 10 }}>
      <Field label="Hiệu xe" placeholder="Honda Vision" value={draft.vehicleModel} onChangeText={(vehicleModel) => update({ vehicleModel })} />
      <Field label="Biển số xe" placeholder="43B1-xxx.xx" value={draft.plate} onChangeText={(plate) => update({ plate: plate.toUpperCase() })} autoCapitalize="characters" />
    </View>
    <View>
      <FieldLabel>Khu vực muốn nhận đơn</FieldLabel>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {options.areas.map((area) => (
          <Chip key={area} label={area} selected={draft.areas.includes(area)} onPress={() => update({ areas: toggleIn(draft.areas, area) })} height={44} />
        ))}
      </View>
    </View>
    <View>
      <FieldLabel>Ca có thể chạy</FieldLabel>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        {options.shifts.map((shift) => (
          <ShiftButton key={shift.id} label={shift.label} time={shift.time} selected={draft.shifts.includes(shift.id)} onPress={() => update({ shifts: toggleIn(draft.shifts, shift.id) })} />
        ))}
      </View>
    </View>
  </>
);

const DocumentsStep = ({ options, draft, update }: StepProps) => {
  const photoDocs = options.documents.filter((doc) => doc.id !== 'bankAccount');
  return (
    <>
      <View style={{ gap: 4 }}>
        <Text variant="heading" style={{ fontSize: 16 }}>
          Giấy tờ
        </Text>
        <Text variant="caption">Chụp rõ 4 góc, không loá. Thiếu giấy tờ nào bạn có thể bổ sung khi đến quán.</Text>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
        {photoDocs.map((doc) => (
          <DocumentTile key={doc.id} id={doc.id} label={doc.label} value={draft.documents[doc.id]} onPicked={(ref) => update({ documents: { ...draft.documents, [doc.id]: ref } })} />
        ))}
      </View>
    </>
  );
};

const PayoutStep = ({ draft, update }: Omit<StepProps, 'options'>) => (
  <>
    <View style={{ gap: 4 }}>
      <Text variant="heading" style={{ fontSize: 16 }}>
        Nhận tiền
      </Text>
      <Text variant="caption">Phí ship được chuyển vào tài khoản này theo ngày, sau khi đối soát COD với quán.</Text>
    </View>
    <Field
      label="Tài khoản ngân hàng"
      placeholder="Vietcombank · 0123456789 · NGUYEN VAN A"
      value={draft.bankAccount}
      onChangeText={(bankAccount) => update({ bankAccount: bankAccount.slice(0, 60) })}
    />
    <View style={{ backgroundColor: colors.background, borderRadius: radii.md, padding: 12 }}>
      <Text variant="caption">Có thể bỏ trống và bổ sung sau, nhưng quán chỉ duyệt khi hồ sơ đủ giấy tờ và tài khoản nhận tiền.</Text>
    </View>
  </>
);
